/* CUSTOM_MARKETINGOPS_BY_CLAUDE — DNI number swap (Phase C).
 *
 * Rewrites the displayed business phone number to the visitor's channel tracking
 * number on CTA surfaces, so a call to that number identifies the ad channel it came
 * from. Channel is read from window.BSFSJourney (utm-journey-tracker.js, loaded just
 * before this); the number map comes from GET /dni-map (cached 24h in localStorage,
 * with a baked-in fallback so first paint / a worker outage still resolves a number).
 *
 * SAFE BY DENYLIST: swaps every occurrence of our displayed number(s) EXCEPT inside
 * <script> (schema.org LocalBusiness JSON-LD), <style>, and [data-dni-keep].
 * That covers the announcement bar, product trust-bullets, contact + landing CTAs, the
 * footer, etc. with zero template edits, while the schema NAP stays on the real number.
 *
 * Only our OWN displayed numbers (current 720-515-3585 + new 720-650-0055) are ever
 * touched — never a customer's number. Full design: CALL-TRACKING-DNI-PLAN.md
 *
 * Classifier MUST mirror handlers/admin.js mktChannelFromTouch — see memory
 * attribution-channel-classifier-drift.
 */
(function () {
  'use strict';

  var MAP_URL = 'https://rfq-worker.ryan-29b.workers.dev/dni-map';
  var CACHE_KEY = 'bsfs_dni_map';
  var CACHE_TTL = 24 * 60 * 60 * 1000;

  // Baked-in fallback — keep in sync with rfq-worker src/config.js DNI_MAP.
  var FALLBACK_MAP = { version: 1, channels: {
    direct:       { e164: '+17206500055', display: '720-650-0055' },
    google:       { e164: '+17207073761', display: '720-707-3761' },
    meta:         { e164: '+17208097477', display: '720-809-7477' },
    bing:         { e164: '+17207066220', display: '720-706-6220' },
    openai:       { e164: '+17205132552', display: '720-513-2552' },
    'paid-other': { e164: '+17207065959', display: '720-706-5959' }
  } };

  // The displayed numbers we're allowed to REPLACE (current + post-NAP-migration),
  // digits/last-10. We only ever swap these.
  var SOURCE_DIGITS = ['7205153585', '7206500055'];

  var PAID_MEDIA_RE = /^(cpc|ppc|cpm|cpa|paid_?social|paidsocial|paid-social|display|banner|retargeting|remarketing|paid|affiliate|feed|reels|stories|story|instagram_stories|facebook_stories|messenger_stories|instagram_reels|facebook_reels|audience_network|messenger|messenger_inbox|video_feeds|instream_video|marketplace|right_hand_column)$/i;

  function digits(s) { return String(s || '').replace(/\D+/g, ''); }
  function last10(s) { var d = digits(s); return d.length >= 10 ? d.slice(-10) : ''; }

  function isPaidTouch(t) {
    if (!t) return false;
    if (PAID_MEDIA_RE.test(String(t.utm_medium || ''))) return true;
    return !!(t.gclid || t.fbclid || t.msclkid || t.oppref || t.gbraid || t.wbraid || t.gad_source || t.gad_campaignid);
  }
  function classifyTouch(t) {
    if (!t) return 'organic';
    var src = String(t.utm_source || '').toLowerCase().trim();
    if (src) {
      if (src === 'facebook' || src === 'fb' || src === 'instagram' || src === 'ig') return 'meta';
      if (src === 'google' || src === 'adwords') return 'google';
      if (src === 'openai' || src === 'chatgpt') return 'openai';
      return src; // bing / microsoft / etc. pass through
    }
    if (t.fbclid) return 'meta';
    if (t.msclkid) return 'bing';
    if (t.oppref) return 'openai';
    if (t.gclid || t.gbraid || t.wbraid || t.gad_source || t.gad_campaignid) return 'google';
    return 'organic';
  }
  // First paid touch wins (mirrors mktLeadChannel's firstPaidTouch preference).
  function resolveChannel() {
    var journey = [];
    try { journey = (window.BSFSJourney && window.BSFSJourney.read()) || []; } catch (e) {}
    var paid = [];
    for (var i = 0; i < journey.length; i++) if (isPaidTouch(journey[i])) paid.push(journey[i]);
    if (!paid.length) return 'direct';
    var ch = classifyTouch(paid[0]);
    return ch === 'organic' ? 'direct' : ch;
  }

  function refresh() {
    try {
      fetch(MAP_URL, { mode: 'cors' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (m) {
          if (m && m.channels) { try { localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), map: m })); } catch (e) {} }
        }).catch(function () {});
    } catch (e) {}
  }
  function getMap() {
    try {
      var raw = localStorage.getItem(CACHE_KEY);
      if (raw) { var o = JSON.parse(raw); if (o && o.ts && (Date.now() - o.ts) < CACHE_TTL && o.map && o.map.channels) { refresh(); return o.map; } }
    } catch (e) {}
    refresh(); // background-refresh for next time
    return FALLBACK_MAP; // use immediately — no paint delay
  }

  function buildNumberRegex(sources) {
    // Match each source number in common formats ((720) 515-3585, 720-515-3585,
    // 720.515.3585, 7205153585). Anchored to OUR digits only. `\(?` consumes a leading
    // paren when present; we deliberately do NOT match leading whitespace, so a preceding
    // word ("Call 720-…") keeps its space after replacement.
    var parts = sources.map(function (d) {
      return d.slice(0, 3) + '[\\s.\\-\\)]*' + d.slice(3, 6) + '[\\s.\\-]*' + d.slice(6);
    });
    return new RegExp('\\(?(?:' + parts.join('|') + ')', 'g');
  }

  // Protect the schema NAP + non-visible contexts. CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-10-14):
  // the FOOTER now swaps too. It was skipped for "NAP consistency", but crawlers never arrive
  // through an ad, so they always see the baseline number; only an ad visitor's own browser
  // changes. The schema.org LocalBusiness JSON-LD (inside <script>) is what search engines read
  // as the business phone, and it stays skipped. Wrap anything else in [data-dni-keep].
  var SKIP_SELECTOR = '[data-dni-keep], script, style, noscript, template';
  function skip(node) {
    var el = (node.nodeType === 1) ? node : (node.parentElement || node.parentNode);
    if (!el || el.nodeType !== 1 || !el.closest) return true; // no element context → skip to be safe
    return !!el.closest(SKIP_SELECTOR);
  }

  function run() {
    var channel = resolveChannel();
    // Organic/direct visitors see the page's baseline number (the displayed/NAP number,
    // which the static NAP migration sets to 720-650-0055). Only PAID channels get swapped
    // to a tracking number — so this never disturbs organic visitors.
    if (channel === 'direct') return;
    var map = getMap();
    var chans = (map && map.channels) || FALLBACK_MAP.channels;
    var entry = chans[channel] || chans['paid-other'] || null; // unknown paid channel → catch-all
    if (!entry || !entry.e164) return;
    var targetDigits = last10(entry.e164);
    if (!targetDigits) return;
    var shown = targetDigits;
    // A Google visitor who already got a forwarding number earlier this visit goes straight to it
    // (one swap, no wait on Google). googleCallSwap still asks Google and corrects it if it changed.
    var gfn = channel === 'google' ? readGfn() : '';
    if (gfn) { swapNumbers(SOURCE_DIGITS, '+1' + gfn, fmt(gfn)); shown = gfn; }
    else swapNumbers(SOURCE_DIGITS, entry.e164, entry.display);
    if (channel === 'google') googleCallSwap(targetDigits, shown);
  }

  function fmt(d) { return d.slice(0, 3) + '-' + d.slice(3, 6) + '-' + d.slice(6); }

  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (2026-10-14) — the forwarding number Google gave this visitor,
   * remembered for the tab's session and at most 2 hours, so later pages skip the
   * main → 720-707-3761 → GFN double switch. sessionStorage dies with the tab, which keeps a stale
   * number from outliving Google's assignment; the TTL caps a tab left open all day. */
  var GFN_KEY = 'bsfs_gfn';
  var GFN_TTL = 2 * 60 * 60 * 1000;
  function readGfn() {
    try {
      var o = JSON.parse(sessionStorage.getItem(GFN_KEY) || 'null');
      if (o && o.d && /^\d{10}$/.test(o.d) && Date.now() - o.ts < GFN_TTL) return o.d;
    } catch (e) {}
    return '';
  }
  function saveGfn(d) {
    try { sessionStorage.setItem(GFN_KEY, JSON.stringify({ d: d, ts: Date.now() })); } catch (e) {}
  }

  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (2026-10-14) — Google Ads website call conversions.
   * For a Google-channel visitor, ask Google for its forwarding number and swap OUR Google
   * number (720-707-3761, just placed by run()) for it. Callback mode, per the agency: Google's
   * own DOM scan can run before run() has put 720-707-3761 on the page and then finds nothing,
   * so Google only HANDS us the number and this script places it with the same skip rules
   * (schema NAP untouched, tel: hrefs updated). The forwarding number rings through to
   * 720-707-3761, so Twilio still logs the call as Google. Google only answers for a real ad
   * click; otherwise no callback fires and 720-707-3761 stays. gtag.js loads ONLY for these
   * visitors (Shopify's Google & YouTube app loads its own copy on every page, without the call
   * config). Where Google SENDS the call is NOT this code: it is the "Phone Calls" conversion
   * action's Destination number in Google Ads (fixed 2026-10-14 from 720-515-3585, which made
   * calls skip Twilio). */
  var GADS_ID = 'AW-16799722465';
  var GADS_CALL_LABEL = GADS_ID + '/zP66COvggogaEOHX3co-'; // "Phone Calls" (website) conversion action
  function googleCallSwap(ourDigits, shown) {
    try {
      window.dataLayer = window.dataLayer || [];
      if (typeof window.gtag !== 'function') {
        window.gtag = function () { window.dataLayer.push(arguments); };
      }
      var s = document.createElement('script');
      s.async = true;
      s.src = 'https://www.googletagmanager.com/gtag/js?id=' + GADS_ID;
      document.head.appendChild(s);
      window.gtag('js', new Date());
      window.gtag('config', GADS_ID);
      window.gtag('config', GADS_CALL_LABEL, {
        phone_conversion_number: fmt(ourDigits),
        phone_conversion_callback: function (formatted, mobile) {
          var d = last10(mobile) || last10(formatted);
          if (!d || d === ourDigits) return;
          saveGfn(d);
          if (d === shown) return;
          swapNumbers([shown], '+1' + d, fmt(d));
          shown = d;
        }
      });
    } catch (e) {} // never break the page: 720-707-3761 stays on screen
  }

  // Swap every displayed occurrence of `sources` (last-10 digit strings) to the target number,
  // except inside SKIP_SELECTOR.
  function swapNumbers(sources, targetE164, targetDisplay) {
    var targetDigits = last10(targetE164);

    // 1) tel: links
    var links = document.querySelectorAll('a[href^="tel:"]');
    for (var i = 0; i < links.length; i++) {
      var a = links[i];
      if (skip(a)) continue;
      var hd = last10(a.getAttribute('href'));
      if (sources.indexOf(hd) === -1 || hd === targetDigits) continue;
      a.setAttribute('href', 'tel:' + targetE164);
      // NOTE (DNI audit 2026-07-24): do NOT rewrite a.textContent here. Setting it
      // nukes the anchor's child nodes — the SVG phone icon + any "Call " prefix
      // (e.g. custom-thank-you.liquid, custom-quote-page.liquid) — on paid visits.
      // The href swap above is sufficient; the visible number is swapped safely by
      // the TreeWalker text-node pass below, which preserves surrounding nodes.
    }

    // 2) visible text occurrences
    var RE = buildNumberRegex(sources);
    var tails = sources.map(function (d) { return d.slice(-4); });
    var walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, null, false);
    var nodes = [], n;
    while ((n = walker.nextNode())) {
      var v = n.nodeValue;
      if (!v || !tails.some(function (t) { return v.indexOf(t) !== -1; })) continue; // cheap prefilter
      if (skip(n)) continue;
      nodes.push(n);
    }
    for (var j = 0; j < nodes.length; j++) {
      var val = nodes[j].nodeValue;
      var nv = val.replace(RE, function (m) { return last10(m) === targetDigits ? m : targetDisplay; });
      if (nv !== val) nodes[j].nodeValue = nv;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
